import { Editor as ToastEditor } from "@toast-ui/react-editor";
import { NoticeAttachmentFileRequest } from "apis/data-contracts";
import Board from "components/Common/Board/Board";
import AttachmentField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField/AttachmentField";
import Editor from "components/document/Create/Editor";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Modal from "components/foundation/Modal/Modal";
import Toast from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import useBeforeUnload from "features/common/hooks/useBeforeUnload";
import useNoticeUpdateMutation from "features/notice/hooks/mutations/useNoticeUpdateMutation";
import useNoticeQuery from "features/notice/hooks/queries/useNoticeQuery";
import SaveWarningModal from "pages/user/dashboard/detail/SaveWarningModal";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

const NoticeDashboardEditWebPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { mutate } = useNoticeUpdateMutation();
  const { data } = useNoticeQuery(id as string);
  const noticeDetail = data.data.result.notice;

  const {
    blocking,
    process,
    isBlock,
    enableUnloadTracking,
    disableUnloadTracking,
  } = useBeforeUnload({
    isInitialTracking: false,
  });

  const [title, setTitle] = React.useState(noticeDetail.title);

  const [nameErrorToastOpen, setNameErrorToastOpen] = React.useState(false);
  const [isFileErrorModal, setIsFileErrorModal] = React.useState(false);
  const [isNameEmptyOpen, setIsNameEmptyOpen] = React.useState(false);
  const [isContentEmptyOpen, setIsContentEmptyOpen] = React.useState(false);

  const [isEditModal, setIsEditModal] = React.useState(false);

  const editorRef = React.useRef<ToastEditor | null>(null);
  const [attachments, setAttachments] = useState<NoticeAttachmentFileRequest[]>(
    noticeDetail?.noticeAttachmentFileList.map((attachment) => ({
      fileName: attachment.fileName,
      noticeFileUrl: attachment.fileUrl,
    })),
  );

  const handleSubmit = () => {
    if (!editorRef.current) return;
    if (title.length < 2) return;
    if (!id) return;

    const editorInstance = editorRef.current.getInstance();
    const value = editorInstance?.getHTML() || "";

    mutate(
      {
        uuid: id,
        title: title,
        content: value,
        //TODO: 파일 업로드 연결 필요
        noticeAttachmentFileList: attachments,
      },
      {
        onSuccess: (data) => {
          disableUnloadTracking();
          navigate(
            ROUTER_PATH.notice.dashboard.detail(data.data.result.notice.uuid),
          );
        },
      },
    );
  };

  if (!id) {
    navigate(ROUTER_PATH.notice.dashboard._path);
  }

  const handleEditClick = () => {
    const content = editorRef.current?.getInstance().getMarkdown();

    if (title.length > 100) {
      setNameErrorToastOpen(true);
    } else if (title.length === 0) {
      setIsNameEmptyOpen(true);
    } else if (!content) {
      setIsContentEmptyOpen(true);
    } else {
      setIsEditModal(true);
    }
  };

  useEffect(() => {
    (title.length > 0 ||
      attachments?.[0] ||
      (editorRef.current?.getInstance().getSelectedText().length ?? 0) > 1) &&
      enableUnloadTracking();
    return () => disableUnloadTracking();
  }, [disableUnloadTracking, enableUnloadTracking, attachments, title.length]);

  // 기존 첨부파일 불러오기
  useEffect(() => {
    if (noticeDetail) {
      setAttachments(
        noticeDetail.noticeAttachmentFileList.map(({ fileName, fileUrl }) => ({
          noticeFileUrl: fileUrl,
          fileName,
        })),
      );
    }
  }, [noticeDetail]);

  return (
    <NoticeCreatePageLayout>
      <Toast.Message
        open={nameErrorToastOpen}
        onOpenChange={setNameErrorToastOpen}
      >
        제목은 최대 100자까지 입력이 가능합니다.
      </Toast.Message>
      <Modal.Root open={isNameEmptyOpen} onOpenChange={setIsNameEmptyOpen}>
        <Modal.Body title="제목 기입 필수" onConfirm>
          제목은 필수 항목 입니다. 제목 기입 후 다시 등록해 주세요.
        </Modal.Body>
      </Modal.Root>
      <Modal.Root
        open={isContentEmptyOpen}
        onOpenChange={setIsContentEmptyOpen}
      >
        <Modal.Body title="내용 기입 필수" onConfirm>
          내용은 필수 항목 입니다. 내용 기입 후 다시 등록해 주세요.
        </Modal.Body>
      </Modal.Root>
      <Modal.Root open={isFileErrorModal} onOpenChange={setIsFileErrorModal}>
        <Modal.Body
          title="30MB 이하 파일만 등록 가능합니다."
          onConfirm={() => setIsFileErrorModal(false)}
        >
          파일 용량을 확인해 주세요.
        </Modal.Body>
      </Modal.Root>

      <Modal.Root open={isEditModal} onOpenChange={setIsEditModal}>
        <Modal.Body
          title="공지사항을 수정하시겠습니까?"
          onConfirm={handleSubmit}
          onCancel
        />
      </Modal.Root>

      <SaveWarningModal
        isOpen={isBlock}
        onConfirm={process}
        onCancel={blocking}
      />
      <NoticeCreatePageLayoutHeader>
        <h1>공지사항 수정</h1>
        <div
          style={{
            display: "flex",
            gap: "1.6rem",
          }}
        >
          <Link to={ROUTER_PATH.notice.dashboard._path}>
            <Button size="large" variant="tertiary">
              뒤로
            </Button>
          </Link>
          <Button size="large" type="submit" onClick={handleEditClick}>
            수정
          </Button>
        </div>
      </NoticeCreatePageLayoutHeader>
      <NoticeCreateContentBoard>
        <NoticeInput
          placeholder="제목"
          value={title}
          onChange={(e) => {
            if (e.target.value.length > 100) {
              setNameErrorToastOpen(true);
            }
            setTitle(e.target.value);
          }}
        />
        <Divider />
        <Editor
          placeholder="본문 내용을 입력해 주세요."
          ref={editorRef}
          defaultValue={noticeDetail.content}
        />
      </NoticeCreateContentBoard>
      <NoticeCreateAttachBoard>
        <AttachmentField
          idField="documentFileUrl"
          attachments={attachments.map((attachment) => ({
            fileHash: attachment.fileHash,
            fileName: attachment.fileName,
            documentFileUrl: attachment.noticeFileUrl,
          }))}
          maxAttachments={1}
          uploadParams={{
            fileType: "NOTICE",
            isOverwrite: true,
            allowedExtensions: [".png"],
          }}
          onChangeAttachments={(attachments) => {
            setAttachments(
              attachments.map((attachment) => ({
                fileName: attachment.fileName,
                fileHash: attachment.fileHash,
                noticeFileUrl: attachment.documentFileUrl,
              })),
            );
          }}
        />
      </NoticeCreateAttachBoard>
    </NoticeCreatePageLayout>
  );
};

export default NoticeDashboardEditWebPage;

const NoticeCreatePageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const NoticeCreateContentBoard = styled(Board)`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .toastui-editor-ww-container {
    background-color: ${({ theme }) => theme.color.background.white} !important;
  }
`;

const NoticeCreateAttachBoard = styled(Board)`
  padding: 4rem;
`;

const NoticeCreatePageLayoutHeader = styled(Board)`
  padding: 3rem 4rem;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px; /* 128.571% */
    letter-spacing: -0.56px;
  }
`;

const NoticeInput = styled.input`
  border: none;
  outline: none;
  font-size: 24px;
  font-weight: 600;
  line-height: 36px; /* 150% */
  letter-spacing: -0.72px;

  ::placeholder {
    color: ${({ theme }) => theme.color.text.placeholder};
  }
`;
